import type { Book } from "./book";

import MiniSearch from "minisearch";

import { log } from "../../log";
import { queryClient } from "../../query";
import { data } from "./data";

export type Query = {
  read?: "read" | "reading" | "unread";
  owned?: boolean;
  tbr?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
};

export function buildSearchQuery(query: Query) {
  return {
    queryKey: ["books", { ...query }],
    async queryFn() {
      const page = query.page ?? 1;
      const pageSize = query.pageSize ?? 10;

      const startIndex = (page - 1) * pageSize;
      const endIndex = page * pageSize;

      let results = data.filter((book) => {
        if (query.read !== undefined && (book.read ?? "unread") !== query.read) {
          return false;
        }

        if (query.owned !== undefined && (book.owned ?? false) !== query.owned) {
          return false;
        }

        if (query.tbr !== undefined && (book.tbr ?? false) != query.tbr) {
          return false;
        }

        return true;
      });

      if (query.q !== undefined && results.length > 0) {
        log.info("Unfiltered results", { results });

        if (query.q.startsWith("author:")) {
          const authorName = query.q.slice(7);
          results = results.filter((book) => book.author?.some((author) => author.name === authorName));
        } else if (query.q.startsWith("series:")) {
          const seriesName = query.q.slice(7);
          results = results.filter((book) => book.series?.some((series) => series.name === seriesName));
        } else if (query.q.startsWith("universe:")) {
          const universeName = query.q.slice(9);
          results = results.filter((book) => book.universe?.some((universe) => universe === universeName));
        } else {
          const miniSearch = new MiniSearch({
            fields: ["title", "description", "author.name", "isbn", "subjects", "series", "universe"],
            idField: "id",

            extractField: (document: Book, fieldName) => {
              // Custom field extraction for nested fields like author.name
              if (fieldName === "author.name") {
                // Handle array of authors
                if (document.author && Array.isArray(document.author)) {
                  return document.author.map((author) => author.name).join(" ");
                }
                return "";
              }

              // Handle ISBN arrays
              if (fieldName === "isbn") {
                if (Array.isArray(document.isbn)) {
                  return document.isbn.join(" ");
                }
                return document.isbn || "";
              }

              // Handle Subjects arrays
              if (fieldName === "subjects") {
                if (Array.isArray(document.subjects)) {
                  return document.subjects.join(" ");
                }
                return document.subjects || "";
              }

              // Handle Series arrays
              if (fieldName === "series") {
                if (Array.isArray(document.series)) {
                  return document.series.join(" ");
                }
                return document.series || "";
              }

              // Handle Universe arrays
              if (fieldName === "universe") {
                if (Array.isArray(document.universe)) {
                  return document.universe.join(" ");
                }
                return document.universe || "";
              }

              // Default field access
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              return (document as any)[fieldName];
            },
            searchOptions: {
              boost: {
                title: 2,
                "author.name": 1.5
              },
              prefix: true,
              fuzzy: (term) => (term.length >= 3 ? 0.1 : false)
            }
          });
          miniSearch.addAll(results);
          const searchResults = miniSearch.search(query.q);

          log.info("Filtered results", { searchResults });

          results = searchResults
            .map((r) => r.id)
            .map((id) => results.find((book) => book.id === id))
            .filter((book) => book !== undefined);
        }
      }

      return {
        data: results.slice(startIndex, endIndex),
        pagination: {
          total: results.length,
          startIndex,
          endIndex: Math.min(endIndex, results.length),
          page,
          pageSize,
          pages: Math.ceil(results.length / pageSize)
        }
      };
    }
  };
}

export async function preloadBooks(query: Query) {
  await queryClient.ensureQueryData(buildSearchQuery(query));
}
